/**
 * Card Type Badge Component
 *
 * Displays card brand logo based on card type
 */

interface CardTypeBadgeProps {
  cardType: string;
  size?: 'small' | 'medium' | 'large';
}

export const CardTypeBadge = ({
  cardType,
  size = 'medium',
}: CardTypeBadgeProps) => {
  const getSize = () => {
    switch (size) {
      case 'small':
        return { width: 32, height: 20 };
      case 'large':
        return { width: 64, height: 40 };
      default:
        return { width: 48, height: 30 };
    }
  };

  const { width, height } = getSize();

  switch (cardType.toUpperCase()) {
    case 'VISA':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 32 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="0.5" y="0.5" width="31" height="19" rx="1.5" fill="white" />
          <rect
            x="0.5"
            y="0.5"
            width="31"
            height="19"
            rx="1.5"
            stroke="#F2F2F2"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.3128 13.1824H8.40449L6.97351 7.72313C6.90559 7.47201 6.76137 7.25 6.54924 7.14536C6.01983 6.8824 5.43646 6.67313 4.80005 6.56759V6.3574H7.87415C8.29842 6.3574 8.61663 6.67313 8.66966 7.03981L9.41214 10.9778L11.3195 6.3574H13.1748L10.3128 13.1824ZM14.2355 13.1824H12.4332L13.9173 6.3574H15.7195L14.2355 13.1824ZM18.051 8.24814C18.1041 7.88055 18.4223 7.67036 18.7935 7.67036C19.3769 7.61759 20.0124 7.72314 20.5427 7.98518L20.8609 6.51573C20.3306 6.30554 19.7472 6.2 19.2178 6.2C17.4686 6.2 16.1958 7.14536 16.1958 8.45741C16.1958 9.45555 17.0974 9.97964 17.7338 10.2954C18.4223 10.6102 18.6874 10.8204 18.6344 11.1352C18.6344 11.6074 18.1041 11.8176 17.5747 11.8176C16.9382 11.8176 16.3018 11.6602 15.7194 11.3972L15.4012 12.8676C16.0376 13.1297 16.7261 13.2352 17.3625 13.2352C19.3238 13.2871 20.5427 12.3426 20.5427 10.925C20.5427 9.13982 18.051 9.03519 18.051 8.24814ZM26.8501 13.1824L25.4191 6.3574H23.882C23.5638 6.3574 23.2456 6.56759 23.1396 6.8824L20.4897 13.1824H22.345L22.7153 12.1852H24.9948L25.207 13.1824H26.8501ZM24.1472 8.19536L24.6766 10.7676H23.1926L24.1472 8.19536Z"
            fill="#172B85"
          />
        </svg>
      );

    case 'MASTERCARD':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 32 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="0.5" y="0.5" width="31" height="19" rx="1.5" fill="white" />
          <rect
            x="0.5"
            y="0.5"
            width="31"
            height="19"
            rx="1.5"
            stroke="#F2F2F2"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.6167 14.8371C14.5557 15.7537 13.1795 16.307 11.6756 16.307C8.32008 16.307 5.59985 13.552 5.59985 10.1535C5.59985 6.75502 8.32008 4 11.6756 4C13.1795 4 14.5557 4.55335 15.6167 5.46999C16.6776 4.55336 18.0539 4.00001 19.5577 4.00001C22.9133 4.00001 25.6335 6.75503 25.6335 10.1535C25.6335 13.552 22.9133 16.307 19.5577 16.307C18.0539 16.307 16.6776 15.7537 15.6167 14.8371Z"
            fill="#ED0006"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.6166 14.8371C16.923 13.7084 17.7513 12.029 17.7513 10.1535C17.7513 8.27812 16.923 6.59865 15.6166 5.46998C16.6775 4.55336 18.0538 4.00002 19.5576 4.00002C22.9132 4.00002 25.6334 6.75504 25.6334 10.1535C25.6334 13.552 22.9132 16.3071 19.5576 16.3071C18.0538 16.3071 16.6775 15.7537 15.6166 14.8371Z"
            fill="#F9A000"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.6166 14.8371C16.923 13.7084 17.7514 12.029 17.7514 10.1536C17.7514 8.27819 16.923 6.59875 15.6166 5.47008C14.3103 6.59875 13.4819 8.27819 13.4819 10.1536C13.4819 12.029 14.3103 13.7084 15.6166 14.8371Z"
            fill="#FF5E00"
          />
        </svg>
      );

    case 'AMEX':
    case 'AMERICAN EXPRESS':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 32 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="0.5"
            y="0.5"
            width="31"
            height="19"
            rx="1.5"
            fill="#1F72CD"
          />
          <rect
            x="0.5"
            y="0.5"
            width="31"
            height="19"
            rx="1.5"
            stroke="#F2F2F2"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.14716 6.2L3.19995 12.7221H6.72817L7.16557 11.6822H8.16536L8.60275 12.7221H12.4863V11.9284L12.8324 12.7221H14.8413L15.1873 11.9116V12.7221H23.2641L24.2462 11.7092L25.1658 12.7221L29.3142 12.7305L26.3577 9.47924L29.3142 6.2H25.2301L24.2741 7.19416L23.3835 6.2H14.597L13.8425 7.8834L13.0703 6.2H9.54946V6.96667L9.15778 6.2H6.14716ZM6.82987 7.12614H8.54969L10.5046 11.5488V7.12614H12.3886L13.8985 10.2972L15.29 7.12614H17.1646V11.8062H16.024L16.0147 8.13893L14.3517 11.8062H13.3314L11.6591 8.13893V11.8062H9.31258L8.86771 10.757H6.46429L6.02034 11.8052H4.76309L6.82987 7.12614ZM22.8462 7.12614H18.2082V11.8034H22.7744L24.2462 10.2533L25.6648 11.8034H27.1477L24.9923 9.47832L27.1477 7.12614H25.7291L24.2648 8.65846L22.8462 7.12614ZM7.66649 7.91798L6.87466 9.78704H8.45741L7.66649 7.91798ZM19.3535 8.94947V8.09513V8.09431H22.2475L23.5103 9.46061L22.1915 10.8344H19.3535V9.90169H21.8837V8.94947H19.3535Z"
            fill="white"
          />
        </svg>
      );

    default:
      // UNKNOWN card type handling (aligned with BT Pay Checkout UI behavior)
      // Shows generic grey card badge - does NOT block payment at UI level
      // Backend (Adyen) will validate if card is actually supported
      // Unsupported cards (Discover, Diners, JCB, etc.) will be rejected by backend
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 48 30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="48" height="30" rx="4" fill="#6B7280" />
          <rect x="4" y="8" width="40" height="4" rx="1" fill="#9CA3AF" />
          <rect x="4" y="16" width="20" height="3" rx="1" fill="#9CA3AF" />
          <rect x="28" y="16" width="16" height="3" rx="1" fill="#9CA3AF" />
        </svg>
      );
  }
};
